import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { MapPin, Phone } from 'lucide-react';
import { Spinner } from '@/shared/ui/Spinner';
import { formatDate } from '@/shared/utils/format';
import { apiErrorMessage, referralsApi } from '../api/referralsApi';
import { referralKeys } from '../queryKeys';
import styles from './publicPage.module.css';

/** /refer/p/:token — the patient's copy from their text: where to go, why, and whom to call. */
export function PatientReferralPage() {
  const { token = '' } = useParams<{ token: string }>();
  const view = useQuery({
    queryKey: referralKeys.patientView(token),
    queryFn: () => referralsApi.patientView(token),
    enabled: Boolean(token),
    retry: false,
  });

  if (view.isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.center}>
          <Spinner />
        </div>
      </div>
    );
  }
  if (view.isError || !view.data) {
    return (
      <div className={styles.page}>
        <div className={styles.center}>
          <h1 className={styles.title}>Referral unavailable</h1>
          <p className={styles.lead}>{apiErrorMessage(view.error, 'This link is not valid anymore.')}</p>
        </div>
      </div>
    );
  }

  const r = view.data;
  const address = [r.toPractice.address, [r.toPractice.city, r.toPractice.state].filter(Boolean).join(', '), r.toPractice.zip].filter(Boolean).join(' · ');
  const chosen = r.options.find((o) => o.chosen) ?? null;
  const multi = r.options.length > 1 && !chosen;
  if (multi) {
    return (
      <div className={styles.page}>
        <div className={`${styles.container} ${styles.narrow}`}>
          <div className={styles.card}>
            <h1 className={styles.title}>Hi {r.patientFirstName}, here's your referral</h1>
            <p className={styles.lead}>
              {r.fromPractice.name}
              {r.referringDoctorName ? ` (${r.referringDoctorName})` : ''} has referred you to a specialist
              {r.reasons.length ? ` for ${r.reasons.join(', ').toLowerCase()}` : ''}
              {r.siteLabel ? ` (${r.siteLabel})` : ''}. Any of these offices can see you; call whichever suits you best.
            </p>
            <h4 className={styles.sectionTitle}>Your options</h4>
            <ol className={styles.optionList}>
              {r.options.map((o) => {
                const addr = [o.address, [o.city, o.state].filter(Boolean).join(', '), o.zip].filter(Boolean).join(' · ');
                return (
                  <li key={o.position} className={styles.option}>
                    <div className={styles.optionName}>
                      {o.name}
                      {o.declined && <span className={styles.optionTag}>Unable to see you</span>}
                    </div>
                    {addr && <div className={styles.optionMeta}>{addr}</div>}
                    {o.phone && <div className={styles.optionMeta}>{o.phone}</div>}
                    {!o.declined && (
                      <div className={styles.ctaRow}>
                        {o.phone && (
                          <a className={styles.cta} href={`tel:${o.phone}`}>
                            <Phone size={14} /> Call to schedule
                          </a>
                        )}
                        {o.mapsUrl && (
                          <a className={styles.ctaSecondary} href={o.mapsUrl} target="_blank" rel="noreferrer">
                            <MapPin size={14} /> Directions
                          </a>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
            <p className={styles.note}>
              Each office already has your referral details{r.sentAt ? ` (sent ${formatDate(r.sentAt)})` : ''}. Questions? Call {r.fromPractice.name}
              {r.fromPractice.phone ? ` at ${r.fromPractice.phone}` : ''}.
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className={styles.page}>
      <div className={`${styles.container} ${styles.narrow}`}>
        <div className={styles.card}>
          <h1 className={styles.title}>Hi {r.patientFirstName}, here's your referral</h1>
          <p className={styles.lead}>
            {r.fromPractice.name}
            {r.referringDoctorName ? ` (${r.referringDoctorName})` : ''} has referred you to <strong>{r.toPractice.name}</strong>
            {r.reasons.length ? ` for ${r.reasons.join(', ').toLowerCase()}` : ''}
            {r.siteLabel ? ` (${r.siteLabel})` : ''}.
          </p>
          {r.status === 'scheduled' && r.scheduledFor && (
            <p className={styles.lead}>
              Your appointment is on <strong>{formatDate(r.scheduledFor, 'EEEE, MMMM d, yyyy h:mm a')}</strong>.
            </p>
          )}
          <h4 className={styles.sectionTitle}>Where to go</h4>
          <div className={styles.kv}>
            <span className={styles.k}>Office</span>
            <span className={styles.v}>{r.toPractice.name}</span>
            {address && (
              <>
                <span className={styles.k}>Address</span>
                <span className={styles.v}>{address}</span>
              </>
            )}
            {r.toPractice.phone && (
              <>
                <span className={styles.k}>Phone</span>
                <span className={styles.v}>{r.toPractice.phone}</span>
              </>
            )}
          </div>
          <div className={styles.ctaRow}>
            {r.toPractice.phone && (
              <a className={styles.cta} href={`tel:${r.toPractice.phone}`}>
                <Phone size={14} /> Call to schedule
              </a>
            )}
            {r.toPractice.mapsUrl && (
              <a className={styles.ctaSecondary} href={r.toPractice.mapsUrl} target="_blank" rel="noreferrer">
                <MapPin size={14} /> Directions
              </a>
            )}
          </div>
          <p className={styles.note}>
            The office has your referral details{r.sentAt ? ` (sent ${formatDate(r.sentAt)})` : ''}. Questions? Call {r.fromPractice.name}
            {r.fromPractice.phone ? ` at ${r.fromPractice.phone}` : ''}.
          </p>
        </div>
      </div>
    </div>
  );
}
